/**
 * Game juice: plugs the juice engine and the trauma shake into Catch'n'Merge.
 *
 * Presentation only. It listens to EventBus notifications and reads game state (positions,
 * layout, the flask system) but never writes gameplay state, never calls the game RNG, and
 * never moves the DOM or the canvas element (input maps through their rects): the shake is
 * applied inside the renderer, to the Pixi world layers or as a ctx.translate in Canvas 2D.
 *
 * Frame protocol (core/game.js):
 *   beginFrame(ctx)  after the sky is drawn: advance the pause-aware clock, step particles and
 *                    trauma, poll render-side moments (drop landings, falling trails); in Canvas
 *                    2D also save + translate the context by the shake and draw the back layer
 *                    there, under the world the frame draws next (as Pixi's juiceBack is);
 *   present()        at the shared end-of-frame hook: draw the particles (Pixi sprites, or the
 *                    2D front layer), hand the shake offset to the playfield, restore the 2D
 *                    context.
 *
 * The effect recipes live in juice-moments.js; this file only finds where they happen.
 * See docs/CARTOON_ART.md "Juice API".
 */
import { createJuiceEngine, LAYER_BACK, LAYER_FRONT } from "./juice-engine.js";
import { bakeJuiceAtlas, bakeJuiceAtlasLarge } from "./juice-atlas.js";
import { createJuiceCanvasDrawer } from "./juice-canvas.js";
import { createTraumaShake } from "./trauma-shake.js";
import { getInkTextSprite } from "./juice-text.js";
import { setJuiceFrameSource } from "./juice-frames.js";
import { TIER_COLORS } from "./juice-palettes.js";
import {
  JUICE_TRAUMA,
  boulderHitFx, flaskBreakFx, shieldBlockFx, boulderBurstFx, catchFx, gemFx, shieldPickupFx,
  reviveFx, mergeFx, dragonCappedFx, landingFx, shakeBoosterFx, poofFx, shrinkFx, healFx,
  jarReviveFx, fallTrailFor,
} from "./juice-moments.js";
import { areRuntimeParticlesEnabled } from "../../core/runtime-flags.js";
import { prefersReducedMotion } from "../../core/sky-background-prefs.js";
import * as C from "../../config/constants.js";

var MAX_CLEAR_POOFS = 24;
/** How long a merge's pop-in runs (s), and how far from the merge point its body is looked for. */
var MERGE_POP_S = 0.45;
var MERGE_FIND_REACH = 1.6;
/** The large atlas page is baked this long after the base one at the latest (idle time first). */
var LARGE_ATLAS_DELAY_MS = 1500;

/** Shake tuning (px at trauma 1 for a ~150 px flask; scaled with the layout). */
export var JUICE_SHAKE = {
  worldMaxX: 18,
  worldMaxY: 15,
  worldMaxAngle: 0.012,
  worldDecay: 1.5,
  /** Largest merge / landing kick (px at layout unit 1), however many land at once. */
  worldMaxKick: 9,
  jarDecay: 3.2,
  jarRate: 2.4,
  jarBasePx: 14,
  /** The jar wobble never exceeds this many times the kick that started it (rapid Shake taps). */
  jarMaxGain: 1.6,
};

function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}
function nowMs() {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

/**
 * @param {object} game the Game instance (read-only use)
 * @param {{ on: Function }} bus EventBus
 * @param {{ pixiModule?: () => Promise<object> }} [opts]
 */
export function attachGameJuice(game, bus, opts) {
  var o = opts || {};
  var engine = createJuiceEngine({ enabled: areRuntimeParticlesEnabled });
  var world = createTraumaShake({
    decay: JUICE_SHAKE.worldDecay,
    maxX: JUICE_SHAKE.worldMaxX,
    maxY: JUICE_SHAKE.worldMaxY,
    maxAngle: JUICE_SHAKE.worldMaxAngle,
    maxKick: JUICE_SHAKE.worldMaxKick,
  });
  // Jar wobble (Merge): unit amplitude scaled by jarAmp px; mostly sideways, like a slosh.
  // jarKickPx is the kick that started the current wobble: boosters scale from it, never from
  // the running amplitude, so rapid taps cannot compound it.
  var jar = createTraumaShake({ decay: JUICE_SHAKE.jarDecay, maxX: 1, maxY: 0.3, rate: JUICE_SHAKE.jarRate });
  var jarAmp = JUICE_SHAKE.jarBasePx;
  var jarKickPx = JUICE_SHAKE.jarBasePx;

  var atlas = null;
  var atlasTried = false;
  var largeAtlasState = 0; // 0 not asked, 1 scheduled, 2 merged (or failed)
  var largePageFrom = -1; // index of the first large page once merged
  var pixiSide = null; // { playfield, textures, back, front }
  var pixiLoading = false;
  var pixiLoad = null; // the drawer load in flight (shared by ensurePixi and warm)
  var canvasDrawer = null;
  var clock = { src: null, last: null };
  var frameBegun = false;
  var shakenCtx = null;
  // Canvas 2D: the back layer was drawn at beginFrame (under the world), so present skips it.
  var backDrawn = false;
  var offset = { x: 0, y: 0, angle: 0 };
  var reduced = prefersReducedMotion();
  var reducedCheckedAt = 0;
  var autoFallTrails = true;
  // Dev slow motion (window.__juiceDebug.juice.setTimeScale) for reviewing effects frame by frame.
  var timeScale = 1;
  var landingWatch = typeof WeakSet === "function" ? new WeakSet() : null;
  // Recent merges (panel space) and the body each one made, so a body drawer can pop exactly
  // that creature in (getMergePop): { x, y, r, tier, t0, id } (id null until resolved).
  var recentMerges = [];

  engine.setTextBaker(function (text, color, size) {
    var dpr = game && game.pixi && game.pixi.resolution ? game.pixi.resolution : undefined;
    return getInkTextSprite(text, color, size, { dpr: dpr });
  });

  // ---- clock: pause-aware, per stage ----

  function readClock() {
    var raw = nowMs();
    var sm = game.stateMachine;
    if (sm && typeof sm.is === "function") {
      var rp = game.runPhase;
      if (sm.is("fall") && rp && typeof rp._getAnimTime === "function") {
        return { src: "fall", t: rp._getAnimTime(raw) };
      }
      if (sm.is("shake") && typeof game._getShakeDrawTime === "function") {
        return { src: "shake", t: game._getShakeDrawTime(raw) };
      }
    }
    return { src: "raw", t: raw };
  }

  function stepClock() {
    var c = readClock();
    var dt = 0;
    // A new clock source (stage change) starts at dt 0 instead of jumping.
    if (clock.src === c.src && clock.last != null && isFinite(c.t)) dt = (c.t - clock.last) / 1000;
    clock.src = c.src;
    clock.last = c.t;
    if (!(dt > 0)) dt = 0;
    if (dt > 0.05) dt = 0.05;
    if (typeof game._isSimPaused === "function" && game._isSimPaused()) dt = 0;
    return dt * timeScale;
  }

  // ---- layout helpers ----

  function layoutUnit() {
    var s = game.layout && game.layout.scale;
    var w = s && s.runwayFlaskDisplayWidth;
    return clamp(w > 0 ? w / 150 : 1, 0.6, 2);
  }

  function isLite() {
    var cfg = C.RUN_CONFIG || {};
    return !!(C.SKY_BACKGROUND_MODES && cfg.SKY_BACKGROUND_EFFECTIVE === C.SKY_BACKGROUND_MODES.STATIC);
  }

  function refreshReducedMotion(t) {
    if (t - reducedCheckedAt < 2000) return;
    reducedCheckedAt = t;
    reduced = prefersReducedMotion();
  }

  /** The Fall catcher flask in canvas px: centre x, top, size, run origin. */
  function fallFlask() {
    var rp = game.runPhase;
    if (!rp || !rp.player || typeof game._getFallCatcherFlaskDims !== "function") return null;
    var dims = game._getFallCatcherFlaskDims();
    if (!dims) return null;
    var ox = rp.runOriginX || 0;
    var oy = rp.runOriginY || 0;
    return { cx: ox + rp.player.x, top: oy + dims.flaskTopY, w: dims.flaskW, h: dims.flaskH, ox: ox, oy: oy };
  }

  function findFallEntry(obj) {
    var rp = game.runPhase;
    var fp = rp && rp.fallPhysics;
    if (!fp || !fp.bodies || !obj) return null;
    for (var i = 0; i < fp.bodies.length; i++) {
      if (fp.bodies[i].obj === obj) return fp.bodies[i];
    }
    return null;
  }

  function mergeOrigin() {
    var L = game.layout;
    if (!L || !L.flaskPanelOrigin) return null;
    var w = getJarWobble();
    return { x: L.flaskPanelOrigin.x + w.x, y: L.flaskPanelOrigin.y + w.y };
  }

  /** The Merge jar in canvas px (mouth centre, floor, half width). */
  function mergeJar() {
    var sys = game.flaskSystem;
    var g = sys && sys._flaskGeometry;
    var o0 = mergeOrigin();
    if (!g || !o0) return null;
    return {
      cx: o0.x + (g.neckLeft + g.neckRight) / 2,
      top: o0.y + g.T,
      bottom: o0.y + g.bodyBottom,
      halfW: (g.neckRight - g.neckLeft) / 2,
    };
  }

  function tierRadius(tier) {
    var sys = game.flaskSystem;
    var radii = sys && sys.bodyRadiusByTier;
    var r = radii && radii[tier];
    return r > 0 ? r : 20 * layoutUnit();
  }

  // ---- shake ----

  /**
   * Apply what a moment asked for: { add } stacks trauma, { min } raises it to at least,
   * { kick: { px, angle } } punches the view (px at layout unit 1, scaled in computeOffset).
   */
  function applyTrauma(t) {
    if (!t) return;
    if (t.add) world.add(t.add);
    if (t.min) world.atLeast(t.min);
    if (t.kick && t.kick.px > 0) world.kick(t.kick.px, t.kick.angle);
  }

  function computeOffset() {
    if (reduced) return { x: 0, y: 0, angle: 0 };
    return world.offset(engine.time, layoutUnit());
  }

  /** Merge jar wobble (px), the internals of Game#_getFlaskVisualShakeOffset. */
  function getJarWobble() {
    if (jar.amount <= 0.0004) return { x: 0, y: 0 };
    var w = jar.offset(engine.time, jarAmp * (reduced ? 0.5 : 1));
    return { x: w.x, y: w.y };
  }

  // ---- Fall moments ----

  function onBoulder(payload) {
    var f = fallFlask();
    if (!f) return;
    var entry = findFallEntry(payload && payload.obj);
    var cx = f.cx, cy = f.top + f.h * 0.55;
    var bx = cx, by = f.top, br = f.w * 0.12;
    if (entry && entry.body && entry.body.position) {
      bx = f.ox + entry.body.position.x;
      by = f.oy + entry.body.position.y;
      br = entry.body.circleRadius > 0 ? entry.body.circleRadius : br;
    }
    // Contact: the boulder's edge facing the flask's middle.
    var dx = cx - bx, dy = cy - by;
    var d = Math.sqrt(dx * dx + dy * dy) || 1;
    var px = bx + (dx / d) * br, py = by + (dy / d) * br;
    if (payload && payload.isHpDepleted) {
      applyTrauma(flaskBreakFx(engine, { x: px, y: py, cx: cx, cy: cy, flaskW: f.w, unit: layoutUnit() }));
    } else {
      applyTrauma(boulderHitFx(engine, { x: px, y: py, flaskW: f.w, rockR: br, unit: layoutUnit() }));
    }
  }

  function shieldGeom(f) {
    return { cx: f.cx, cy: f.top + f.h / 2, R: (Math.sqrt(f.w * f.w + f.h * f.h) / 2) * 1.05, topY: f.top - f.h * 0.05, flaskW: f.w };
  }

  function onShieldBlock() {
    var f = fallFlask();
    if (f) applyTrauma(shieldBlockFx(engine, shieldGeom(f)));
  }

  function onBoulderExplode(payload) {
    var f = fallFlask();
    var entry = findFallEntry(payload && payload.obj);
    if (!entry || !entry.body || !f) return;
    var r = entry.body.circleRadius > 0 ? entry.body.circleRadius : f.w * 0.1;
    applyTrauma(boulderBurstFx(engine, {
      x: f.ox + entry.body.position.x,
      y: f.oy + entry.body.position.y,
      r: r,
      // Hit the mouth lid (no shield): dust is kicked out along the rim too.
      rimY: payload && payload.shieldBlocked ? null : f.top + f.h * 0.04,
    }));
  }

  function onCatch(payload) {
    if (!payload || payload.x == null || payload.y == null) return;
    var f = fallFlask();
    catchFx(engine, { x: payload.x, y: payload.y, flaskW: f ? f.w : 150 * layoutUnit(), tier: payload.tier });
  }

  function onGem() {
    var f = fallFlask();
    if (f) gemFx(engine, { x: f.cx, y: f.top + f.h * 0.04, flaskW: f.w });
  }

  function onShieldPickup() {
    var f = fallFlask();
    if (f) shieldPickupFx(engine, { cx: f.cx, cy: f.top + f.h / 2, R: Math.sqrt(f.w * f.w + f.h * f.h) / 2 });
  }

  function onFallRevive() {
    var f = fallFlask();
    if (f) reviveFx(engine, { cx: f.cx, cy: f.top + f.h * 0.5, w: f.w, h: f.h });
  }

  // ---- Merge moments ----

  function onMerge(payload) {
    if (!payload || !payload.position) return;
    var o0 = mergeOrigin();
    if (!o0) return;
    var x = o0.x + payload.position.x;
    var y = o0.y + payload.position.y;
    if (payload.capped) {
      var top = TIER_COLORS.length - 1;
      applyTrauma(dragonCappedFx(engine, { x: x, y: y, R: tierRadius(payload.fromTier != null ? payload.fromTier : top), unit: layoutUnit() }));
      return;
    }
    if (payload.toTier == null) return;
    var t = Math.max(0, Math.floor(Number(payload.toTier) || 0));
    if (recentMerges.length >= 12) recentMerges.shift();
    var m = { x: payload.position.x, y: payload.position.y, r: tierRadius(t), tier: t, t0: engine.time, id: null };
    recentMerges.push(m);
    // The flask system adds the new body before it announces the merge; if it is not there yet
    // (another host), beginFrame keeps looking for it.
    resolveMergeBody(m);
    var chain = typeof C.getActiveEvolutionChain === "function" ? C.getActiveEvolutionChain() : null;
    var maxTier = chain && chain.length ? chain.length - 1 : TIER_COLORS.length - 1;
    applyTrauma(mergeFx(engine, { x: x, y: y, R: tierRadius(t), tier: t, maxTier: maxTier, unit: layoutUnit() }));
  }

  /**
   * Find the body a merge made: the nearest body of the new tier to the merge point (within
   * MERGE_FIND_REACH radii) that no other recent merge has claimed. Stores its Matter id.
   */
  function resolveMergeBody(m) {
    if (m.id != null) return true;
    var sys = game.flaskSystem;
    var list = sys && sys.bodies;
    if (!list || !list.length) return false;
    var best = null, bestD = Infinity;
    var reach = m.r * MERGE_FIND_REACH;
    for (var i = 0; i < list.length; i++) {
      var meta = list[i];
      if (!meta || !meta.body || !meta.body.position || meta.tier !== m.tier) continue;
      if (isClaimed(meta.body.id, m)) continue;
      var dx = meta.body.position.x - m.x, dy = meta.body.position.y - m.y;
      var d = dx * dx + dy * dy;
      if (d < bestD && d <= reach * reach) {
        bestD = d;
        best = meta;
      }
    }
    if (best) m.id = best.body.id;
    return !!best;
  }

  function isClaimed(id, except) {
    for (var i = 0; i < recentMerges.length; i++) {
      if (recentMerges[i] !== except && recentMerges[i].id === id) return true;
    }
    return false;
  }

  /** Drop finished pops; resolve any merge whose body was not there at the event. */
  function tickMergePops() {
    var n = 0;
    for (var i = 0; i < recentMerges.length; i++) {
      var m = recentMerges[i];
      if (engine.time - m.t0 > MERGE_POP_S) continue;
      if (m.id == null) resolveMergeBody(m);
      recentMerges[n++] = m;
    }
    recentMerges.length = n;
  }

  /**
   * Pop-in scale for a body (see api.getMergePop): `who` is a flask body meta, a Matter body,
   * or a body id.
   */
  function mergePopFor(who) {
    if (!recentMerges.length || who == null) return 1;
    var id = typeof who === "object" ? (who.body ? who.body.id : who.id) : who;
    if (id == null) return 1;
    for (var i = recentMerges.length - 1; i >= 0; i--) {
      var m = recentMerges[i];
      if (m.id !== id) continue;
      var age = engine.time - m.t0;
      if (age > MERGE_POP_S || age < 0) return 1;
      return 1 - 0.45 * Math.exp(-age * 9) * Math.cos(age * 22);
    }
    return 1;
  }

  /** Drop landings have no event: watch each dropped body's `_dropLanding` flag flip. */
  function pollDropLandings() {
    var sys = game.flaskSystem;
    if (!sys || !sys.bodies || !landingWatch) return;
    var sm = game.stateMachine;
    if (!sm || typeof sm.is !== "function" || !sm.is("shake")) return;
    for (var i = 0; i < sys.bodies.length; i++) {
      var meta = sys.bodies[i];
      if (!meta || !meta.body) continue;
      if (meta._dropLanding) {
        landingWatch.add(meta);
      } else if (landingWatch.has(meta)) {
        landingWatch.delete(meta);
        var o0 = mergeOrigin();
        if (!o0) continue;
        var r = meta.radiusY > 0 ? meta.radiusY : (meta.radiusX > 0 ? meta.radiusX : 16);
        applyTrauma(landingFx(engine, {
          x: o0.x + meta.body.position.x,
          y: o0.y + meta.body.position.y + r * 0.9,
          r: r,
          tier: meta.tier,
          unit: layoutUnit(),
        }));
      }
    }
  }

  function onShakeBooster(payload) {
    var mult = payload && payload.multiplier > 0 ? payload.multiplier : 1;
    // From the kick that started this wobble, never from the running amplitude, and capped.
    jarAmp = Math.min(jarKickPx * JUICE_SHAKE.jarMaxGain, Math.max(jarAmp, jarKickPx * (0.75 + 0.3 * mult)));
    var j = mergeJar();
    if (j) applyTrauma(shakeBoosterFx(engine, { cx: j.cx, bottom: j.bottom, halfW: j.halfW, mult: mult, unit: layoutUnit() }));
  }

  function onClear(payload) {
    var list = payload && payload.bodies;
    var o0 = mergeOrigin();
    if (!list || !list.length || !o0) return;
    var n = Math.min(list.length, engine.lite ? MAX_CLEAR_POOFS / 2 : MAX_CLEAR_POOFS);
    applyTrauma({ add: JUICE_TRAUMA.clear });
    for (var i = 0; i < n; i++) {
      var b = list[i];
      if (!b || b.x == null || b.y == null) continue;
      poofFx(engine, { x: o0.x + b.x, y: o0.y + b.y, r: b.radius > 0 ? b.radius : 14, tier: b.tier, i: i });
    }
  }

  function onShrink(payload) {
    if (!payload || payload.x == null || payload.y == null) return;
    var o0 = mergeOrigin();
    if (!o0) return;
    var r = payload.radius > 0 ? payload.radius : 16;
    shrinkFx(engine, { x: o0.x + payload.x, y: o0.y + payload.y, r: r, newR: shrinkTargetRadius(payload, r) });
  }

  /** The radius the shrinking body is heading for (read from the flask's shrink tween). */
  function shrinkTargetRadius(payload, r) {
    var sys = game.flaskSystem;
    var list = sys && sys.activeShrinkMotions;
    if (!list || !list.length) return 0;
    var best = 0, bestD = Infinity;
    for (var i = 0; i < list.length; i++) {
      var a = list[i];
      var b = a && a.meta && a.meta.body;
      if (!b || !b.position || !(a.newRadiusX > 0)) continue;
      var dx = b.position.x - payload.x, dy = b.position.y - payload.y;
      var d = dx * dx + dy * dy;
      if (d < bestD && d <= r * r) {
        bestD = d;
        best = a.newRadiusX;
      }
    }
    return best;
  }

  function onHeal() {
    var j = mergeJar();
    if (j) healFx(engine, { cx: j.cx, top: j.top, halfW: j.halfW });
  }

  function onMergeRevive() {
    var j = mergeJar();
    if (j) jarReviveFx(engine, { cx: j.cx, cy: (j.top + j.bottom) / 2, halfW: j.halfW });
  }

  // ---- falling-object trails (render-side; a later Fall pass may take this over) ----

  function pollFallTrails() {
    if (!autoFallTrails) return;
    var sm = game.stateMachine;
    var rp = game.runPhase;
    if (!sm || typeof sm.is !== "function" || !sm.is("fall") || !rp || !rp.fallPhysics) return;
    var bodies = rp.fallPhysics.bodies;
    if (!bodies || !bodies.length) return;
    var ox = rp.runOriginX || 0, oy = rp.runOriginY || 0;
    var U = layoutUnit();
    // The sea line (run space): nothing trails into the water; puffs already out fade on their own.
    var sea = rp.runHeight > 0 ? rp.runHeight : Infinity;
    for (var i = 0; i < bodies.length; i++) {
      var e = bodies[i];
      var obj = e.obj, body = e.body;
      if (!obj || !body || e.suction || e.exploding || obj.collected || obj.caught) continue;
      var v = body.velocity;
      var speed = v ? Math.sqrt(v.x * v.x + v.y * v.y) : 0;
      if (speed < 0.6) continue;
      var r = body.circleRadius > 0 ? body.circleRadius : 12;
      if (body.position.y - r * 0.55 > sea) continue;
      // Emit from behind the object along its travel.
      var bx = ox + body.position.x - (v.x / speed) * r * 0.55;
      var by = oy + body.position.y - (v.y / speed) * r * 0.55;
      if (by < -r) continue;
      var type = typeof obj.getType === "function" ? obj.getType() : obj.type;
      var trail = fallTrailFor(type, r, U, obj.tier);
      if (trail) engine.trail(body.id, bx, by, trail);
    }
  }

  // ---- rendering ----

  function ensureAtlas() {
    if (atlas || atlasTried) return atlas;
    atlasTried = true;
    try {
      atlas = bakeJuiceAtlas();
    } catch (err) {
      atlas = null;
      if (typeof console !== "undefined") console.warn("[juice] atlas bake failed", err);
    }
    if (atlas) scheduleLargeAtlas();
    return atlas;
  }

  /**
   * The large frames (one more page, see juice-atlas.js) are baked off the boot path, in idle
   * time, and appended to the atlas; until then the engine draws the base frames. On Pixi the
   * new page is uploaded right away (still idle time), not on the first big blast.
   */
  function scheduleLargeAtlas() {
    if (largeAtlasState) return;
    largeAtlasState = 1;
    var run = mergeLargeAtlas;
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(run, { timeout: LARGE_ATLAS_DELAY_MS });
    } else if (typeof setTimeout === "function") {
      setTimeout(run, 400);
    }
  }

  /** Bake the large page now (once) and append it to the atlas; on Pixi it is uploaded at once. */
  function mergeLargeAtlas() {
    if (largeAtlasState === 2) return;
    largeAtlasState = 2;
    if (!atlas) return;
    var large = null;
    try {
      large = bakeJuiceAtlasLarge();
    } catch (err) {
      if (typeof console !== "undefined") console.warn("[juice] large atlas bake failed", err);
    }
    if (!large) return;
    var base = atlas.pages.length;
    for (var p = 0; p < large.pages.length; p++) atlas.pages.push(large.pages[p]);
    Object.keys(large.frames).forEach(function (key) {
      var f = large.frames[key];
      atlas.frames[key] = { page: f.page + base, x: f.x, y: f.y, w: f.w, h: f.h };
    });
    largePageFrom = base;
    preuploadPages(base, atlas.pages.length);
  }

  function preuploadPages(from, to) {
    var side = pixiSide;
    var renderer = side && side.playfield && side.playfield.app && side.playfield.app.renderer;
    var sys = renderer && renderer.texture;
    if (!sys || typeof sys.initSource !== "function" || typeof side.textures.source !== "function") return;
    for (var p = from; p < to; p++) {
      try {
        var src = side.textures.source(p);
        if (src) sys.initSource(src);
      } catch (_e) { /* the first draw uploads it instead */ }
    }
  }

  /** Device px per canvas px: the Pixi resolution, or the 2D canvas's own DPR. */
  function renderResolution() {
    var pf = game.pixi;
    if (pf && pf.resolution > 0) return pf.resolution;
    if (typeof game._getCanvasDpr === "function") {
      var d = game._getCanvasDpr();
      if (d > 0) return d;
    }
    return 1;
  }

  function ensurePixi(pf) {
    if (pixiSide && pixiSide.playfield === pf) return pixiSide;
    if (!pf || !pf.juiceFront || !pf.juiceBack) return null;
    if (!ensureAtlas() || pixiLoading) return null;
    loadPixiSide(pf);
    return null;
  }

  /**
   * Load the Pixi drawer and make the atlas textures for playfield `pf` (once; a load in flight
   * is shared). Resolves to the drawer side, or null when it could not be made.
   */
  function loadPixiSide(pf) {
    if (pixiSide && pixiSide.playfield === pf) return Promise.resolve(pixiSide);
    if (pixiLoad) return pixiLoad;
    if (!pf || !pf.juiceFront || !pf.juiceBack || !ensureAtlas()) return Promise.resolve(null);
    // juice-pixi imports pixi.js: load it only on the Pixi path (keeps Canvas 2D and tests light).
    pixiLoading = true;
    var load = o.pixiModule || function () { return import("./juice-pixi.js"); };
    var started;
    try {
      started = Promise.resolve(load());
    } catch (errLoad) {
      started = Promise.reject(errLoad);
    }
    pixiLoad = started.then(function (mod) {
      pixiLoading = false;
      pixiLoad = null;
      if (pixiSide && pixiSide.textures) pixiSide.textures.destroy();
      var textures = mod.createJuiceTextures(atlas);
      pixiSide = {
        playfield: pf,
        textures: textures,
        back: mod.createJuicePixiDrawer(pf.juiceBack, textures),
        front: mod.createJuicePixiDrawer(pf.juiceFront, textures),
      };
      // Other drawers (spawn bursts, score popups) can use the same atlas frames.
      setJuiceFrameSource({ texture: textures.get });
      // A large page baked before the drawers existed: upload it now, off the hot path.
      if (largePageFrom >= 0) preuploadPages(largePageFrom, atlas.pages.length);
      // (Evicted lettering frees its GPU texture by itself: juice-text releases it through
      // texture-hints, which sprite-pool wires to Pixi.)
      return pixiSide;
    }).catch(function (err) {
      pixiLoading = false;
      pixiLoad = null;
      if (typeof console !== "undefined") console.warn("[juice] pixi drawer failed", err);
      return null;
    });
    return pixiLoad;
  }

  function defaultYield() {
    return new Promise(function (resolve) { setTimeout(resolve, 0); });
  }

  /**
   * Loader warm-up (core/play-warmup.js): bake the base and large atlas pages now and, on Pixi,
   * load the drawer, make the page textures and upload every page, so no frame of play bakes
   * or uploads an effect picture. One step per task (`opts.yieldTask` between steps). Canvas
   * 2D: the bakes only.
   * @param {{ yieldTask?: () => Promise<void> }} [opts]
   * @returns {Promise<void>}
   */
  function warm(opts) {
    var yieldTask = (opts && typeof opts.yieldTask === "function") ? opts.yieldTask : defaultYield;
    if (!ensureAtlas()) return Promise.resolve();
    var pf = game && game.pixi;
    return yieldTask().then(function () {
      return pf ? loadPixiSide(pf) : null;
    }).then(function (side) {
      if (side) preuploadPages(0, atlas.pages.length);
      return yieldTask();
    }).then(function () {
      // Bakes the large page and, with the drawer loaded, uploads it.
      mergeLargeAtlas();
    });
  }

  function renderPixi(pf) {
    var side = ensurePixi(pf);
    if (!side) return;
    side.back.begin();
    side.front.begin();
    if (areRuntimeParticlesEnabled()) {
      engine.render(side.back, LAYER_BACK);
      engine.render(side.front, LAYER_FRONT);
    }
    side.back.end();
    side.front.end();
  }

  /**
   * Canvas 2D: draw the given layers into `ctx` (its current transform is the world's).
   * @returns {boolean} whether the context could be drawn into (an idle engine counts)
   */
  function renderCanvas(ctx, layers) {
    if (!ctx || typeof ctx.drawImage !== "function") return false;
    if (!engine.busy || !areRuntimeParticlesEnabled()) return true;
    if (!canvasDrawer) {
      if (!ensureAtlas()) return false;
      canvasDrawer = createJuiceCanvasDrawer(atlas);
    }
    canvasDrawer.begin(ctx);
    for (var i = 0; i < layers.length; i++) engine.render(canvasDrawer, layers[i]);
    canvasDrawer.end();
    return true;
  }

  /**
   * Canvas 2D: draw the back layer (trails, spawn poofs, the starburst behind a new creature,
   * jar bubbles) now, before the world, so the world covers it as in Pixi. beginFrame calls it;
   * a host that draws the world before beginFrame can call it itself. No-op on Pixi (juiceBack
   * already sits under the world sprites) and once per frame.
   */
  function drawBackLayer(ctx) {
    if (game.pixi || backDrawn) return false;
    backDrawn = renderCanvas(ctx, [LAYER_BACK]);
    return backDrawn;
  }

  function beginFrame(ctx) {
    var dt = stepClock();
    refreshReducedMotion(nowMs());
    engine.lite = isLite();
    engine.reduced = reduced;
    engine.unit = layoutUnit();
    engine.resolution = renderResolution();
    engine.update(dt);
    world.update(dt);
    jar.update(dt);
    if (jar.amount <= 0.0004) {
      jarAmp = JUICE_SHAKE.jarBasePx;
      jarKickPx = JUICE_SHAKE.jarBasePx;
    }
    tickMergePops();
    pollDropLandings();
    pollFallTrails();
    offset = computeOffset();
    frameBegun = true;
    backDrawn = false;
    if (game.pixi || !ctx || typeof ctx.save !== "function") return;
    if (offset.x || offset.y || offset.angle) {
      var L = game.layout;
      var cx = L && L.canvasWidth ? L.canvasWidth / 2 : 0;
      var cy = L && L.canvasHeight ? L.canvasHeight / 2 : 0;
      ctx.save();
      ctx.translate(cx + offset.x, cy + offset.y);
      if (offset.angle) ctx.rotate(offset.angle);
      ctx.translate(-cx, -cy);
      shakenCtx = ctx;
    }
    drawBackLayer(ctx);
  }

  function present() {
    if (!frameBegun) beginFrame(null);
    frameBegun = false;
    var pf = game.pixi;
    if (pf) {
      renderPixi(pf);
      if (typeof pf.setWorldOffset === "function") pf.setWorldOffset(offset.x, offset.y, offset.angle);
    } else {
      renderCanvas(shakenCtx || game.ctx, backDrawn ? [LAYER_FRONT] : [LAYER_BACK, LAYER_FRONT]);
    }
    backDrawn = false;
    if (shakenCtx) {
      shakenCtx.restore();
      shakenCtx = null;
    }
  }

  // ---- wiring ----

  if (bus && typeof bus.on === "function") {
    bus.on("fall:boulder", onBoulder);
    bus.on("fall:shield_block", onShieldBlock);
    bus.on("fall:boulder_explode", onBoulderExplode);
    bus.on("fall:catch", onCatch);
    bus.on("fall:gem", onGem);
    bus.on("fall:bonus", onShieldPickup);
    bus.on("fall:revive", onFallRevive);
    bus.on("flask:merge", onMerge);
    bus.on("shake:shake", onShakeBooster);
    bus.on("shake:clear", onClear);
    bus.on("shake:shrink", onShrink);
    bus.on("shake:life_gain", onHeal);
    bus.on("merge:revived", onMergeRevive);
  }

  var api = {
    engine: engine,
    beginFrame: beginFrame,
    present: present,
    drawBackLayer: drawBackLayer,
    warm: warm,
    /** The world shake offset this frame (px / radians); the DOM HP pill follows it. */
    getWorldOffset: function () {
      return offset;
    },
    getJarWobble: getJarWobble,
    /**
     * Pop-in scale for the creature a merge just made: it swells from 0.55 past full size
     * (≈1.13) and settles, over ~0.35 s; 1 for every other body. Keyed by the new body itself
     * (resolved when the merge is announced), so it follows that body wherever it rolls and
     * never touches a neighbour that happens to sit near the merge point.
     * @param {{ body?: { id: number } } | { id: number } | number} who flask body meta, Matter body or id
     */
    getMergePop: mergePopFor,
    /** Jar wobble kick (Game#triggerFlaskVisualShake): `px` amplitude at full trauma. */
    jarKick: function (px) {
      var amp = Number(px);
      if (isFinite(amp) && amp > 0) {
        var live = jar.amount > 0.05;
        jarKickPx = live ? Math.max(jarKickPx, amp) : amp;
        jarAmp = Math.min(jarKickPx * JUICE_SHAKE.jarMaxGain, live ? Math.max(jarAmp, amp) : amp);
      }
      jar.atLeast(1);
    },
    /** FallPhase onBoulderImpact (any boulder contact, shielded or not): a small kick. */
    onBoulderImpact: function () {
      world.add(JUICE_TRAUMA.boulderImpact);
    },
    /** Add screen-shake trauma (0–1). */
    addTrauma: function (amount) {
      world.add(amount);
    },
    get trauma() {
      return world.trauma;
    },
    /** Dev: slow the juice clock (0.1 = ten times slower) to review effects. */
    setTimeScale: function (k) {
      timeScale = k > 0 ? Number(k) : 1;
    },
    /** Trails behind falling objects are emitted here unless a renderer takes them over. */
    setAutoFallTrails: function (on) {
      autoFallTrails = !!on;
    },
    clear: function () {
      engine.clear();
      world.clear();
      jar.clear();
    },
  };
  // Dev builds: a console / capture-script handle for trying effects by hand.
  if (typeof window !== "undefined" && typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV) {
    window.__juiceDebug = { juice: api, game: game, bus: bus };
  }
  return api;
}

/**
 * Snapshot of the bodies a Clear booster is about to remove (panel space), for the poofs.
 * Read-only: copies numbers out of the flask system.
 * @param {{ bodies?: object[] }} flask
 * @param {number[]} tiers
 * @returns {{ x: number, y: number, tier: number, radius: number }[]}
 */
export function collectClearedBodies(flask, tiers) {
  var out = [];
  if (!flask || !flask.bodies || !tiers) return out;
  for (var i = 0; i < flask.bodies.length; i++) {
    var meta = flask.bodies[i];
    if (!meta || !meta.body || !meta.body.position || tiers.indexOf(meta.tier) < 0) continue;
    out.push({
      x: meta.body.position.x,
      y: meta.body.position.y,
      tier: meta.tier,
      radius: meta.radiusX > 0 ? meta.radiusX : 0,
    });
  }
  return out;
}
